import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { formatSpan } from '../../hooks/useSubathonAdmin';
import { SubathonEventRow, SubathonEventType, SubathonTotals } from '../../types/subathon';
import {
  Button,
  ErrorText,
  Muted,
  StatCard,
  StatValue,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Table,
  TableWrap,
  Toolbar,
} from '../ui';

interface Props {
  /** Cambia con cada estado nuevo: sirve para recargar la lista sola. */
  version: number;
  refresh: () => Promise<void>;
}

const TYPE_LABELS: Record<SubathonEventType, string> = {
  follow: 'Follow',
  raid: 'Raid',
  bits: 'Bits',
  sub: 'Sub',
  resub: 'Resub',
  subgift: 'Sub regalada',
  cafecito: 'Cafecito',
  coffee: 'Ko-fi',
  tip: 'Propina',
  chest: 'Cofre',
  manual: 'Ajuste',
};

const SOURCE_LABELS: Record<string, string> = {
  twitch: 'Twitch',
  kofi: 'Ko-fi',
  streamelements: 'StreamElements',
  manual: 'A mano',
};

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.85rem;
`;

const Undone = styled.tr`
  opacity: 0.45;

  td {
    text-decoration: line-through;
  }
`;

const Amount = styled.td`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: #9dffb0;
`;

const Top = styled.ol`
  margin: 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.88rem;

  span {
    color: ${theme.colors.textMuted};
    font-size: 0.8rem;
  }
`;

const SubathonActivityTab: React.FC<Props> = ({ version, refresh }) => {
  const [events, setEvents] = useState<SubathonEventRow[]>([]);
  const [totals, setTotals] = useState<SubathonTotals | null>(null);
  const [includeUndone, setIncludeUndone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/subathon/events', {
        params: includeUndone ? { includeUndone: 1 } : undefined,
      });
      setEvents(data.events || []);
      setTotals(data.totals || null);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo cargar la actividad');
    }
  }, [includeUndone]);

  useEffect(() => {
    load();
  }, [load, version]);

  const undo = async (event: SubathonEventRow) => {
    setBusy(true);
    try {
      await api.post(`/subathon/events/${event.id}/undo`);
      await Promise.all([load(), refresh()]);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo deshacer');
    } finally {
      setBusy(false);
    }
  };

  const byType = totals?.byType || {};

  return (
    <>
      {error && <ErrorText>{error}</ErrorText>}

      {totals && totals.events > 0 && (
        <Stats>
          <StatCard>
            <span>Tiempo sumado</span>
            <StatValue>{formatSpan(totals.seconds * 1000)}</StatValue>
          </StatCard>
          <StatCard>
            <span>Aportes</span>
            <StatValue>{totals.events}</StatValue>
          </StatCard>
          <StatCard>
            <span>Personas</span>
            <StatValue>{totals.top.length}</StatValue>
          </StatCard>
          {Object.entries(byType)
            .sort((a, b) => (b[1]?.seconds || 0) - (a[1]?.seconds || 0))
            .slice(0, 4)
            .map(([type, info]) => (
              <StatCard key={type}>
                <span>{TYPE_LABELS[type as SubathonEventType] || type}</span>
                <StatValue>{formatSpan((info?.seconds || 0) * 1000)}</StatValue>
                <small style={{ color: theme.colors.textMuted }}>
                  {info?.count} {info?.count === 1 ? 'vez' : 'veces'}
                </small>
              </StatCard>
            ))}
        </Stats>
      )}

      <Surface>
        <SurfaceHeader>
          <SurfaceTitle>Todo lo que entró en este extensible</SurfaceTitle>
          <Toolbar>
            <label style={{ fontSize: '0.82rem', color: theme.colors.textMuted, display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={includeUndone}
                onChange={(e) => setIncludeUndone(e.target.checked)}
              />
              Ver los deshechos
            </label>
            <Button $size="sm" $variant="ghost" onClick={load}>
              Actualizar
            </Button>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody $flush>
          {events.length === 0 ? (
            <Muted style={{ padding: '1rem' }}>
              Todavía no hay aportes en este extensible. Cuando entre una sub, bits, un cafecito o
              cualquier otra cosa, va a aparecer acá con el tiempo que sumó.
            </Muted>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Hora</th>
                    <th>Qué pasó</th>
                    <th>Tipo</th>
                    <th>Origen</th>
                    <th>Tiempo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const Row = event.undone ? Undone : 'tr';
                    return (
                      <Row key={event.id}>
                        <td>{new Date(event.createdAt).toLocaleTimeString()}</td>
                        <td>{event.label}</td>
                        <td>{TYPE_LABELS[event.type] || event.type}</td>
                        <td>{SOURCE_LABELS[event.source] || event.source}</td>
                        <Amount>
                          {event.secondsAdded >= 0 ? '+' : ''}
                          {formatSpan(event.secondsAdded * 1000)}
                          {event.secondsCapped > 0 && (
                            <small style={{ color: '#ffc457', display: 'block' }}>
                              tope: -{formatSpan(event.secondsCapped * 1000)}
                            </small>
                          )}
                        </Amount>
                        <td style={{ textAlign: 'right' }}>
                          {!event.undone && (
                            <Button $size="sm" $variant="ghost" disabled={busy} onClick={() => undo(event)}>
                              Deshacer
                            </Button>
                          )}
                        </td>
                      </Row>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </SurfaceBody>
      </Surface>

      {totals && totals.top.length > 0 && (
        <Surface style={{ marginTop: '0.85rem' }}>
          <SurfaceHeader>
            <SurfaceTitle>Quiénes están extendiendo el stream</SurfaceTitle>
          </SurfaceHeader>
          <SurfaceBody>
            <Top>
              {totals.top.slice(0, 10).map((row) => (
                <li key={row.displayName}>
                  {row.displayName} — {formatSpan(row.seconds * 1000)}{' '}
                  <span>({row.events} {row.events === 1 ? 'aporte' : 'aportes'})</span>
                </li>
              ))}
            </Top>
          </SurfaceBody>
        </Surface>
      )}
    </>
  );
};

export default SubathonActivityTab;
